import type { Account, CreateAccountInput, UpdateAccountInput, ReorderAccountsInput } from '@zeni/shared'
import type { Member, CreateMemberInput, UpdateMemberInput } from '@zeni/shared'
import type { Transaction, CreateTransactionInput, UpdateTransactionInput, TransactionFilters, BulkUpdateTransactionsInput, TransferCounterpartQuery, TransferCounterpartCheck } from '@zeni/shared'
import type { Category, CreateCategoryInput, UpdateCategoryInput } from '@zeni/shared'
import type {
  InvestmentSummary, InvestmentProductValuation, InvestmentTrade, InvestmentImportResult,
  Brokerage, CreateBrokerageInput, UpdateBrokerageInput,
  CreateInvestmentProductInput, UpdateInvestmentProductInput,
} from '@zeni/shared'
import type { LifePlanOverview, UpsertLifePlanSettingInput } from '@zeni/shared'
import type { AssetGoal, AssetGoalProgress, UpsertAssetGoalInput } from '@zeni/shared'
import type { CardPaymentForecast } from '@zeni/shared'
import type { MarketQuote, EconomicIndicator } from '@zeni/shared'

const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000') + '/api/v1'

/**
 * アクセストークンの現在値取得とリフレッシュを担う口。AuthProvider が起動時に
 * 登録する（`setAuthBridge`）。API 層が呼び出し元から受け取るトークンは
 * レンダリング時点のスナップショットで古くなりうるため、送信直前に必ずここから
 * 最新のトークンを取り直す。
 */
export interface AuthBridge {
  /** 有効なアクセストークンを返す。失効（間近）なら内部でリフレッシュする */
  getAccessToken: () => Promise<string | null>
  /** 強制的にリフレッシュする。staleToken には 401 になったトークンを渡す */
  refreshAccessToken: (staleToken?: string | null) => Promise<string | null>
}

let authBridge: AuthBridge | null = null

export function setAuthBridge(bridge: AuthBridge | null) {
  authBridge = bridge
}

/**
 * 認証付きリクエスト。アクセストークンは15分で失効するため、
 * 1. 送信前に最新トークンを取り直す（失効間近なら先にリフレッシュ）
 * 2. それでも 401 が返ったら 1 度だけリフレッシュして再送する
 * の二段構えにしている。リフレッシュ自体が失敗した場合は AuthProvider が
 * セッションを破棄し、ProtectedRoute がログイン画面へ誘導する。
 *
 * token が空文字のリクエスト（パスワードリセットなど認証不要な API）は
 * 対象外で、そのまま素通しする。
 */
async function authedFetch(path: string, token: string, options?: RequestInit): Promise<Response> {
  const send = (t: string) =>
    fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        ...(options?.headers ?? {}),
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      credentials: 'include',
    })

  if (!token || !authBridge) return send(token)

  const current = (await authBridge.getAccessToken()) ?? token
  const res = await send(current)
  if (res.status !== 401) return res

  // 再送は 1 度だけ。アクセストークンの中身は { sub, type, exp } のみで、
  // 同一秒内に再発行すると文字列が前回と一致しうるため、
  // 「トークンが変わったか」ではなく「リフレッシュできたか」で判定する。
  const refreshed = await authBridge.refreshAccessToken(current)
  if (!refreshed) return res
  return send(refreshed)
}

async function apiFetch<T>(
  path: string,
  token: string,
  options?: RequestInit,
): Promise<T> {
  const res = await authedFetch(path, token, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
  })
  if (res.status === 204) return undefined as T
  // エラー時はJSON以外（プロキシのHTMLやプレーンテキスト）が返ることがあるため、
  // パースに失敗してもステータスコードを含むメッセージにフォールバックする。
  const raw = await res.text()
  let body: unknown
  try {
    body = raw ? JSON.parse(raw) : undefined
  } catch {
    if (!res.ok) throw new Error(`HTTP ${res.status}: ${raw.slice(0, 200)}`)
    throw new Error('サーバーから不正な応答が返されました')
  }
  if (!res.ok) throw new Error((body as { error?: { message?: string } })?.error?.message ?? `HTTP ${res.status}`)
  return body as T
}

export const accountsApi = {
  list: (token: string, params?: { includeArchived?: boolean }) => {
    const qs = params?.includeArchived ? '?includeArchived=true' : ''
    return apiFetch<{ data: Account[]; meta: { total: number } }>(`/accounts${qs}`, token)
  },
  get: (token: string, id: string) =>
    apiFetch<{ data: Account }>(`/accounts/${id}`, token),
  create: (token: string, data: CreateAccountInput) =>
    apiFetch<{ data: Account }>('/accounts', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (token: string, id: string, data: UpdateAccountInput) =>
    apiFetch<{ data: Account }>(`/accounts/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /**
   * 表示順の付け替え。渡した口座の displayOrder をそのまま上書きするので、
   * 一部だけ動かすときも並びが崩れないよう全口座分を通し番号で渡す
   */
  reorder: (token: string, data: ReorderAccountsInput) =>
    apiFetch<{ data: Account[] }>('/accounts/reorder', token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** 既定はアーカイブ。permanent を指定すると口座の行そのものを削除する */
  delete: (token: string, id: string, options?: { permanent?: boolean }) =>
    apiFetch<void>(`/accounts/${id}${options?.permanent ? '?permanent=true' : ''}`, token, { method: 'DELETE' }),
}

export const cardPaymentsApi = {
  /** クレジットカードの引き落とし予定と、引き落とし先口座の残高見込み */
  forecast: (token: string, params?: { months?: number }) => {
    const qs = params?.months ? `?months=${params.months}` : ''
    return apiFetch<{ data: CardPaymentForecast }>(`/card-payments/forecast${qs}`, token)
  },
}

export const membersApi = {
  list: (token: string) =>
    apiFetch<{ data: Member[]; meta: { total: number } }>('/members', token),
  create: (token: string, data: CreateMemberInput) =>
    apiFetch<{ data: Member }>('/members', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (token: string, id: string, data: UpdateMemberInput) =>
    apiFetch<{ data: Member }>(`/members/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /** 口座が紐づいているメンバーはサーバーが 409 で拒否する */
  delete: (token: string, id: string) =>
    apiFetch<void>(`/members/${id}`, token, { method: 'DELETE' }),
}

/**
 * 絞り込み条件をクエリ文字列にする。
 * 口座・種別・カテゴリは配列で渡せて、サーバーにはカンマ区切りで送る。
 * 未指定（undefined / 空文字 / 'all' / 空配列）の条件は落とす。
 */
function transactionFilterQuery(params?: TransactionFilters): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === '' || value === 'all') continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      search.set(key, value.join(','))
      continue
    }
    search.set(key, String(value))
  }
  return search.toString()
}

export const transactionsApi = {
  list: (token: string, params?: TransactionFilters) => {
    const qs = transactionFilterQuery(params)
    return apiFetch<{ data: Transaction[]; meta: { total: number; limit: number; offset: number } }>(
      `/transactions${qs ? `?${qs}` : ''}`, token
    )
  },
  create: (token: string, data: CreateTransactionInput) =>
    apiFetch<{ data: Transaction }>('/transactions', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (token: string, id: string, data: UpdateTransactionInput) =>
    apiFetch<{ data: Transaction }>(`/transactions/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (token: string, id: string) =>
    apiFetch<void>(`/transactions/${id}`, token, { method: 'DELETE' }),
  bulkUpdate: (token: string, data: BulkUpdateTransactionsInput) =>
    apiFetch<{ data: { updated: number } }>('/transactions/bulk', token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  /**
   * 振替の相手方（振替先口座に同額が入っている取引）を探す。
   * 見つからなくても登録はできるので、結果は注意表示に使うだけ。
   */
  transferCounterpart: (token: string, params: TransferCounterpartQuery) => {
    const qs = new URLSearchParams({
      toAccountId: params.toAccountId,
      amount: String(params.amount),
      date: params.date,
      ...(params.days !== undefined ? { days: String(params.days) } : {}),
      ...(params.excludeId ? { excludeId: params.excludeId } : {}),
    }).toString()
    return apiFetch<{ data: TransferCounterpartCheck }>(`/transactions/transfer-counterpart?${qs}`, token)
  },
  bulkDelete: (token: string, ids: string[]) =>
    apiFetch<{ data: { deleted: number } }>('/transactions/bulk-delete', token, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
}

export const categoriesApi = {
  list: (token: string, params?: { type?: string }) => {
    const qs = params?.type ? `?type=${params.type}` : ''
    return apiFetch<{ data: Category[] }>(`/categories${qs}`, token)
  },
  create: (token: string, data: CreateCategoryInput) =>
    apiFetch<{ data: Category }>('/categories', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (token: string, id: string, data: UpdateCategoryInput) =>
    apiFetch<{ data: Category }>(`/categories/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (token: string, id: string) =>
    apiFetch<void>(`/categories/${id}`, token, { method: 'DELETE' }),
}

export const csvApi = {
  exportTransactions: async (token: string, params?: TransactionFilters): Promise<void> => {
    const qs = transactionFilterQuery(params)
    const res = await authedFetch(`/transactions?format=csv${qs ? `&${qs}` : ''}`, token)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  },
  importTransactions: (token: string, rows: Array<{
    date: string; type: 'income' | 'expense' | 'transfer'; category: string;
    description: string; amount: number; accountId: string;
    toAccountId?: string; currency?: string; note?: string;
  }>) =>
    apiFetch<{ data: { imported: number; skipped: number } }>('/transactions/import', token, {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
}

export const financialInstitutionsApi = {
  list: (token: string, q?: string) => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : ''
    return apiFetch<{ data: Array<{ id: string; name: string; nameKana: string | null; category: string; sortOrder: number; brandColor: string | null }> }>(
      `/financial-institutions${qs}`, token
    )
  },
}

export interface WiseConnection {
  id: string
  profileId: number
  profileType: string
  profileName: string
  authType: string
  lastSyncedAt: string | null
  createdAt: string
}

export interface WiseBalanceRow {
  balanceId: number
  currency: string
  type: string
  amount: number
  name: string | null
  linked: boolean
  wiseAccountId: string | null
  accountId: string | null
  lastSyncedAt: string | null
}

export const wiseApi = {
  listConnections: (token: string) =>
    apiFetch<{ data: WiseConnection[]; meta: { total: number } }>('/integrations/wise/connections', token),

  connect: (token: string, personalToken: string) =>
    apiFetch<{
      data: {
        connections: Array<{
          connectionId: string; profileId: number; type: string; name: string;
          balances: Array<{ balanceId: number; currency: string; type: string; amount: number }>;
        }>
      }
    }>('/integrations/wise/connections', token, {
      method: 'POST',
      body: JSON.stringify({ personalToken }),
    }),

  deleteConnection: (token: string, id: string) =>
    apiFetch<void>(`/integrations/wise/connections/${id}`, token, { method: 'DELETE' }),

  listBalances: (token: string, connectionId: string) =>
    apiFetch<{ data: WiseBalanceRow[]; meta: { total: number } }>(
      `/integrations/wise/connections/${connectionId}/accounts`, token
    ),

  registerBalance: (token: string, connectionId: string, data: { wiseBalanceId: number; syncFromDate?: string }) =>
    apiFetch<{ data: { wiseAccount: { id: string }; account: Account } }>(
      `/integrations/wise/connections/${connectionId}/accounts`, token, {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  sync: (token: string, wiseAccountId: string, options?: { syncFromDate?: string; full?: boolean }) =>
    apiFetch<{
      data: {
        imported: number; updated: number; skipped: number; unrelated: number;
        balance: number | null; lastSyncedAt: string;
      }
    }>(
      `/integrations/wise/accounts/${wiseAccountId}/sync`, token, {
        method: 'POST',
        body: JSON.stringify(options ?? {}),
      }
    ),

  syncStatus: (token: string, wiseAccountId: string) =>
    apiFetch<{
      data: {
        wiseAccountId: string; accountId: string; currency: string;
        lastSyncedAt: string | null; syncFromDate: string | null;
        importedCount: number; balance: number | null;
      }
    }>(`/integrations/wise/accounts/${wiseAccountId}/sync-status`, token),
}

export const authApi = {
  passwordResetRequest: (email: string) =>
    apiFetch<{ data: { message: string } }>('/auth/password-reset', '', {
      method: 'POST',
      body: JSON.stringify({ email }),
      headers: { 'Content-Type': 'application/json' },
    }),
  passwordResetConfirm: (token: string, newPassword: string) =>
    apiFetch<{ data: { message: string } }>('/auth/password-reset/confirm', '', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
      headers: { 'Content-Type': 'application/json' },
    }),
}

export const budgetsApi = {
  list: (token: string, year: number, month: number) =>
    apiFetch<{
      data: {
        year: number; month: number;
        totalBudget: number; totalExpense: number;
        budgets: Array<{
          id: string; categoryId: string; categoryName: string;
          amount: number; actual: number; remaining: number; ratio: number;
        }>
      }
    }>(`/budgets?year=${year}&month=${month}`, token),

  create: (token: string, data: { categoryId: string; year: number; month: number; amount: number }) =>
    apiFetch<{ data: { id: string; categoryId: string; categoryName: string; year: number; month: number; amount: number; createdAt: string; updatedAt: string } }>(
      '/budgets', token, { method: 'POST', body: JSON.stringify(data) }
    ),

  update: (token: string, id: string, amount: number) =>
    apiFetch<{ data: { id: string; amount: number } }>(
      `/budgets/${id}`, token, { method: 'PATCH', body: JSON.stringify({ amount }) }
    ),

  delete: (token: string, id: string) =>
    apiFetch<void>(`/budgets/${id}`, token, { method: 'DELETE' }),

  copy: (token: string, fromYear: number, fromMonth: number) =>
    apiFetch<{ data: { copied: number; skipped: number } }>(
      '/budgets/copy', token, { method: 'POST', body: JSON.stringify({ fromYear, fromMonth }) }
    ),
}

export const recurringApi = {
  list: (token: string, includeArchived = false) =>
    apiFetch<{
      data: Array<{
        id: string; accountId: string; accountName: string;
        type: string; categoryId: string; categoryName: string;
        amount: number; description: string; note?: string;
        pattern: string; patternLabel: string;
        dayOfMonth?: number; dayOfWeek?: number; monthOfYear?: number;
        reminderEnabled: boolean; endDate?: string; excluded?: boolean; isArchived: boolean;
        nextDueDate: string; createdAt: string; updatedAt: string;
      }>;
      meta: { total: number }
    }>(`/recurring-transactions${includeArchived ? '?includeArchived=true' : ''}`, token),

  pending: (token: string) =>
    apiFetch<{
      data: Array<{ id: string; description: string; amount: number; dueDate: string; isPastDue: boolean }>;
      meta: { total: number }
    }>('/recurring-transactions/pending', token),

  create: (token: string, data: {
    accountId: string; type: string; categoryId: string;
    amount: number; description: string; note?: string;
    pattern: string; dayOfMonth?: number; dayOfWeek?: number;
    monthOfYear?: number; reminderEnabled?: boolean; endDate?: string;
    excluded?: boolean;
  }) =>
    apiFetch<{ data: Record<string, unknown> }>('/recurring-transactions', token, {
      method: 'POST', body: JSON.stringify(data)
    }),

  update: (token: string, id: string, data: Partial<{
    amount: number; description: string; note: string;
    pattern: string; dayOfMonth: number; dayOfWeek: number;
    monthOfYear: number; reminderEnabled: boolean; endDate: string;
    excluded: boolean;
  }>) =>
    apiFetch<{ data: Record<string, unknown> }>(`/recurring-transactions/${id}`, token, {
      method: 'PATCH', body: JSON.stringify(data)
    }),

  archive: (token: string, id: string) =>
    apiFetch<void>(`/recurring-transactions/${id}`, token, { method: 'DELETE' }),

  generate: (token: string, id: string, date?: string) =>
    apiFetch<{ data: { transaction: Record<string, unknown> } }>(
      `/recurring-transactions/${id}/generate`, token, {
        method: 'POST', body: JSON.stringify(date ? { date } : {})
      }
    ),
}

export const reportsApi = {
  monthlySummary: (token: string, year: number, month: number) =>
    apiFetch<{
      data: {
        year: number
        month: number
        income: number
        expense: number
        balance: number
        previousMonth: { income: number; expense: number; balance: number }
        expenseChangeRate: number
      }
    }>(`/reports/monthly-summary?year=${year}&month=${month}`, token),

  categoryBreakdown: (token: string, year: number, month: number, type?: string) =>
    apiFetch<{
      data: {
        year: number
        month: number
        type: string
        total: number
        categories: Array<{ categoryId: string; categoryName: string; amount: number; ratio: number }>
      }
    }>(`/reports/category-breakdown?year=${year}&month=${month}${type ? `&type=${type}` : ''}`, token),

  monthlyTrend: (token: string, from: string, to: string) =>
    apiFetch<{
      data: {
        from: string
        to: string
        months: Array<{ year: number; month: number; income: number; expense: number; balance: number }>
      }
    }>(`/reports/monthly-trend?from=${from}&to=${to}`, token),

  dailyCalendar: (token: string, year: number, month: number) =>
    apiFetch<{
      data: {
        year: number
        month: number
        days: Array<{ day: number; income: number; expense: number }>
      }
    }>(`/reports/daily-calendar?year=${year}&month=${month}`, token),

  annualSummary: (token: string, year: number) =>
    apiFetch<{
      data: {
        year: number
        totalIncome: number
        totalExpense: number
        totalBalance: number
        months: Array<{ month: number; income: number; expense: number; balance: number }>
        topExpenseCategories: Array<{ categoryId: string; categoryName: string; amount: number; ratio: number }>
      }
    }>(`/reports/annual-summary?year=${year}`, token),

  annualLedger: (token: string, year: number) =>
    apiFetch<{
      data: {
        year: number
        openingBalance: number
        currentBalance: number
        entries: Array<{
          id: string
          date: string
          type: 'income' | 'expense'
          amount: number
          description: string
          accountId: string
          categoryId: string | null
          categoryName: string | null
          accountName: string
          /** 集計対象外。残高には効くが、収支の合計からは外す */
          excluded?: boolean
        }>
      }
    }>(`/reports/annual-ledger?year=${year}`, token),

  assetTrend: (token: string, from: string, to: string) =>
    apiFetch<{
      data: {
        from: string
        to: string
        months: Array<{
          year: number
          month: number
          totalAssets: number
          accounts: Array<{ accountId: string; accountName: string; balance: number }>
        }>
      }
    }>(`/reports/asset-trend?from=${from}&to=${to}`, token),

  /**
   * カテゴリ × 期間のマトリクス。yearTo を省くと列が 1〜12 月、
   * yearTo を渡すと year〜yearTo の各年が列になる（months 配列が列順）。
   */
  balanceMatrix: (token: string, year: number, yearTo?: number) =>
    apiFetch<{
      data: {
        year: number
        yearTo: number | null
        sections: Array<{
          type: 'income' | 'expense' | 'transfer' | 'investment'
          total: number
          months: number[]
          categories: Array<{
            categoryId: string
            categoryName: string
            categoryIcon: string | null
            categoryColor: string | null
            months: number[]
            total: number
          }>
        }>
      }
    }>(
      `/reports/annual-matrix?year=${year}${yearTo === undefined ? '' : `&yearTo=${yearTo}`}`,
      token,
    ),
}

// ─────────────────────────────────────────────
// 投資（証券会社 > 投資商品カテゴリー > 商品）
// ─────────────────────────────────────────────

export const investmentsApi = {
  summary: (token: string) =>
    apiFetch<{ data: InvestmentSummary }>('/investments/summary', token),

  createBrokerage: (token: string, data: CreateBrokerageInput) =>
    apiFetch<{ data: Brokerage }>('/investments/brokerages', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateBrokerage: (token: string, id: string, data: UpdateBrokerageInput) =>
    apiFetch<{ data: Brokerage }>(`/investments/brokerages/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteBrokerage: (token: string, id: string) =>
    apiFetch<void>(`/investments/brokerages/${id}`, token, { method: 'DELETE' }),

  createProduct: (token: string, data: CreateInvestmentProductInput) =>
    apiFetch<{ data: InvestmentProductValuation }>('/investments/products', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProduct: (token: string, id: string, data: UpdateInvestmentProductInput) =>
    apiFetch<{ data: InvestmentProductValuation }>(`/investments/products/${id}`, token, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteProduct: (token: string, id: string) =>
    apiFetch<void>(`/investments/products/${id}`, token, { method: 'DELETE' }),

  listTrades: (token: string, productId: string) =>
    apiFetch<{ data: InvestmentTrade[]; meta: { total: number } }>(
      `/investments/products/${productId}/trades`, token
    ),

  // CSV は multipart/form-data で送るため、Content-Type はブラウザに決めさせる
  // （境界文字列を自前で組み立てられないので apiFetch は使えない）。
  importCsv: async (token: string, brokerageId: string, file: File): Promise<InvestmentImportResult> => {
    const form = new FormData()
    form.append('file', file)
    const res = await authedFetch(`/investments/brokerages/${brokerageId}/import`, token, {
      method: 'POST',
      body: form,
    })
    const raw = await res.text()
    let body: unknown
    try {
      body = raw ? JSON.parse(raw) : undefined
    } catch {
      throw new Error(`HTTP ${res.status}: ${raw.slice(0, 200)}`)
    }
    if (!res.ok) throw new Error((body as { error?: { message?: string } })?.error?.message ?? `HTTP ${res.status}`)
    return (body as { data: InvestmentImportResult }).data
  },
}

export const lifePlanApi = {
  /**
   * 投資枠（メンバー × 預り区分）と、枠ごとの実績・設定。
   * 「今年」「今月」はクライアントが渡す（Workers は UTC なので年末年始・月初にずれる）。
   * monthsOfYear を渡すと、その年の月ごとの実績も枠ごとに付く（月単位の表示で使う）。
   */
  overview: (
    token: string,
    currentYear: number,
    currentMonth: number,
    monthsOfYear?: number,
  ) =>
    apiFetch<{ data: LifePlanOverview }>(
      `/life-plan?currentYear=${currentYear}&currentMonth=${currentMonth}` +
        (monthsOfYear === undefined ? '' : `&monthsOfYear=${monthsOfYear}`),
      token,
    ),
  /** 枠は保有商品から導出されるので、設定はキーを指定して upsert する */
  saveSetting: (token: string, data: UpsertLifePlanSettingInput) =>
    apiFetch<{ data: Record<string, unknown> }>('/life-plan/settings', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

export const marketApi = {
  /** S&P500・ドル円など、ユーザーに依存しない相場データ */
  quotes: (token: string) =>
    apiFetch<{ data: MarketQuote[] }>('/market/quotes', token),
}

export const economicIndicatorsApi = {
  /** 月例経済報告・GDP・消費者物価指数など、日本の景気指標。表示順で返る */
  list: (token: string) =>
    apiFetch<{ data: EconomicIndicator[] }>('/economic-indicators', token),
}

export const assetGoalApi = {
  /** 今日はクライアントの端末時刻を渡す（Workers は UTC なので日付が JST とずれる） */
  get: (token: string, today: string) =>
    apiFetch<{ data: AssetGoalProgress }>(`/asset-goal?today=${today}`, token),

  save: (token: string, data: UpsertAssetGoalInput) =>
    apiFetch<{ data: AssetGoal }>('/asset-goal', token, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  delete: (token: string) =>
    apiFetch<void>('/asset-goal', token, { method: 'DELETE' }),
}
